import type {
  EmployeeMonthlyAttendanceDay,
  EmployeeMonthlyAttendanceRow,
  EmployeeMonthlyAttendanceTotals,
} from '../types/employeeMonthlyAttendanceReport';
import { businessToday } from './weeklyAttendanceReport';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export const EMPLOYEE_MONTHLY_ATTENDANCE_REPORT_CODE = 'EMPLOYEE_MONTHLY_ATTENDANCE';

export const MONTH_SHORT_NAMES = MONTH_NAMES.map(name => name.slice(0, 3));

export type ReportMonth = { year: number; month: number };

export type MonthlyReportColumn = {
  date: string;
  dayNumber: string;
  weekday: string;
};

/**
 * Totals columns, left to right. The backend PDF / Excel (TOTAL_COLUMNS in
 * employee-monthly-attendance-report.service.js) use the same order.
 */
export const MONTHLY_TOTAL_COLUMNS: readonly {
  key: keyof EmployeeMonthlyAttendanceTotals;
  title: string;
}[] = [
  { key: 'presentDays', title: 'Present Days' },
  { key: 'absentDays', title: 'Absent Days' },
  { key: 'leaveDays', title: 'Leave Days' },
  { key: 'lopDays', title: 'LOP Days' },
  { key: 'halfDays', title: 'Half Days' },
  { key: 'holidays', title: 'Holidays' },
  { key: 'weeklyOffs', title: 'Weekly Offs' },
];

/** The business month containing the anchor date. */
export function currentReportMonth(anchor: Date = businessToday()): ReportMonth {
  return { year: anchor.getFullYear(), month: anchor.getMonth() + 1 };
}

export function shiftReportMonth(period: ReportMonth, deltaMonths: number): ReportMonth {
  const absolute = period.year * 12 + (period.month - 1) + deltaMonths;
  return { year: Math.floor(absolute / 12), month: (absolute % 12) + 1 };
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** "February 2026". */
export function formatReportMonthLabel(year: number, month: number): string {
  if (!Number.isInteger(month) || month < 1 || month > 12) return `${month}/${year}`;
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

/** One column per date, 1st to the last day of the month, with its weekday. */
export function monthlyReportColumns(year: number, month: number): MonthlyReportColumn[] {
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return [];
  }
  const monthText = String(month).padStart(2, '0');
  return Array.from({ length: daysInMonth(year, month) }, (_, index) => {
    const day = index + 1;
    return {
      date: `${year}-${monthText}-${String(day).padStart(2, '0')}`,
      dayNumber: String(day),
      weekday: WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()],
    };
  });
}

/** Day of an employee row by date; "-" when the API sent nothing for it. */
export function monthlyDayCell(
  row: EmployeeMonthlyAttendanceRow,
  date: string,
): EmployeeMonthlyAttendanceDay {
  return (
    row.days.find(day => day.date === date) ?? {
      date,
      code: '-',
      statusCode: null,
      statusLabel: '-',
      notes: [],
    }
  );
}

/** Day total as the backend export prints it ("0", "0.5", "17"). */
export function formatMonthlyTotal(value: number | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return '0';
  return String(Math.round(value * 100) / 100);
}
